// src/components/notifications/NotificationForm.jsx
import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { FormSection, FormRow } from '../../../components/common/form';

const NotificationForm = memo(function NotificationForm({
  formData,
  onInputChange,
  onDateChange,
  validationErrors = {},
  loading = false,
}) {
  const { t } = useTranslation();

  const NOTIFICATION_TYPES = useMemo(() => [
    { value: 'info', label: t('notifications:notification_types.info'), color: 'info' },
    { value: 'warning', label: t('notifications:notification_types.warning'), color: 'warning' },
    { value: 'error', label: t('notifications:notification_types.error'), color: 'error' },
    { value: 'success', label: t('notifications:notification_types.success'), color: 'success' },
    { value: 'task_assigned', label: t('notifications:notification_types.task_assigned'), color: 'primary' },
    { value: 'task_completed', label: t('notifications:notification_types.task_completed'), color: 'success' },
    { value: 'deadline_reminder', label: t('notifications:notification_types.deadline_reminder'), color: 'warning' },
    { value: 'system_update', label: t('notifications:notification_types.system_update'), color: 'info' },
    { value: 'maintenance', label: t('notifications:notification_types.maintenance'), color: 'warning' },
  ], [t]);

  const PRIORITY_LEVELS = useMemo(() => [
    { value: 'low', label: t('notifications:priority.low'), color: 'default' },
    { value: 'normal', label: t('notifications:priority.normal'), color: 'primary' },
    { value: 'high', label: t('notifications:priority.high'), color: 'warning' },
    { value: 'urgent', label: t('notifications:priority.urgent'), color: 'error' },
  ], [t]);

  const handleInputChange = useCallback((field) => (event) => {
    onInputChange(field, event.target.value);
  }, [onInputChange]);

  const handleTitleChange = useCallback((event) => {
    onInputChange('title', event.target.value);
  }, [onInputChange]);

  const handleMessageChange = useCallback((event) => {
    onInputChange('message', event.target.value);
  }, [onInputChange]);

  const handleTypeChange = useCallback((event) => {
    onInputChange('notification_type', event.target.value);
  }, [onInputChange]);

  const handlePriorityChange = useCallback((event) => {
    onInputChange('priority', event.target.value);
  }, [onInputChange]);

  const handleUrlChange = useCallback((event) => {
    onInputChange('onclick_url', event.target.value);
  }, [onInputChange]);

  return (
    <FormSection title={t('notifications:form.content_title')}>
      <TextField
        fullWidth
        label={t('notifications:labels.title')}
        value={formData.title}
        onChange={handleTitleChange}
        error={!!validationErrors.title}
        helperText={validationErrors.title}
        required
        disabled={loading}
        inputProps={{ maxLength: 255 }}
      />
      <TextField
        fullWidth
        label={t('notifications:labels.message')}
        multiline
        rows={4}
        value={formData.message}
        onChange={handleMessageChange}
        error={!!validationErrors.message}
        helperText={validationErrors.message}
        required
        disabled={loading}
      />
      <FormRow>
        <FormControl fullWidth error={!!validationErrors.notification_type}>
          <InputLabel id="notification-form-type-label">{t('notifications:labels.type')}</InputLabel>
          <Select labelId="notification-form-type-label"
            value={formData.notification_type}
            label={t('notifications:labels.type')}
            onChange={handleTypeChange}
            disabled={loading}
          >
            {NOTIFICATION_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label={type.label}
                    size="small"
                    color={type.color}
                    variant="outlined"
                    sx={{ pointerEvents: 'none' }}
                  />
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth error={!!validationErrors.priority}>
          <InputLabel id="notification-form-priority-label">{t('notifications:labels.priority')}</InputLabel>
          <Select labelId="notification-form-priority-label"
            value={formData.priority}
            label={t('notifications:labels.priority')}
            onChange={handlePriorityChange}
            disabled={loading}
          >
            {PRIORITY_LEVELS.map((priority) => (
              <MenuItem key={priority.value} value={priority.value}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label={priority.label}
                    size="small"
                    color={priority.color}
                    variant="outlined"
                    sx={{ pointerEvents: 'none' }}
                  />
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </FormRow>
      <TextField
        fullWidth
        label={t('notifications:form.click_url_label')}
        value={formData.onclick_url}
        onChange={handleUrlChange}
        error={!!validationErrors.onclick_url}
        helperText={validationErrors.onclick_url || t('notifications:form.click_url_help')}
        placeholder="https://example.com/page"
        disabled={loading}
      />
      <DateTimePicker
        label={t('notifications:form.expiration_label')}
        value={formData.expires_at}
        onChange={onDateChange}
        disabled={loading}
        slotProps={{
          textField: {
            fullWidth: true,
            error: !!validationErrors.expires_at,
            helperText: validationErrors.expires_at || t('notifications:form.expiration_help'),
          },
        }}
        minDateTime={new Date()}
      />
    </FormSection>
  );
});

export default NotificationForm;